import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, LogOut, Download, Receipt as ReceiptIcon, Camera, Upload, Plus } from "lucide-react";

const RAW = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${RAW}/api`;

// Driver portal uses its own token key separate from the admin app token.
const TOKEN_KEY = "driver_portal_token";

function dax() {
  const token = localStorage.getItem(TOKEN_KEY);
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const STATUS_PILL = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  rejected: "bg-rose-500/15 text-rose-400 border-rose-500/40",
  paid: "bg-sky-500/15 text-sky-400 border-sky-500/40",
};

export default function DriverPortal() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const w = params.get("w") || "";
  const d = params.get("d") || "";
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const [statements, setStatements] = useState([]);
  const [tab, setTab] = useState("stubs"); // "stubs" | "expenses"
  const [expenses, setExpenses] = useState([]);
  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState({ amount: "", category: "", note: "", file: null, preview: null, company_card_id: "" });
  const [expBusy, setExpBusy] = useState(false);

  // On mount, try reusing an existing driver session.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    dax().get("/driver-portal/me")
      .then((r) => { setMe(r.data); loadStatements(); loadExpenses(); })
      .catch(() => localStorage.removeItem(TOKEN_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatements = async () => {
    try {
      const r = await dax().get("/driver-portal/statements");
      setStatements(r.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setMe(null);
      }
    }
  };

  const loadExpenses = async () => {
    try {
      const r = await dax().get("/driver-portal/expenses");
      setExpenses(r.data || []);
    } catch (e) { /* non-fatal */ }
  };

  const login = async () => {
    if (!w || !d) {
      toast.error("This portal link is missing the fleet/driver code — ask your dispatcher to resend it.");
      return;
    }
    if (!pin || pin.length < 4) {
      toast.error("Enter the 6-digit PIN your dispatcher gave you");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auth/driver-login`, { company_id: w, driver_id: d, pin });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      const mr = await dax().get("/driver-portal/me");
      setMe(mr.data);
      await loadStatements();
      await loadExpenses();
      toast.success(`Welcome, ${r.data.driver.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setMe(null); setStatements([]); setExpenses([]); setPin("");
    nav(`/driver?w=${w}&d=${d}`);
  };

  const downloadPdf = async (sid) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API}/driver-portal/statements/${sid}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("PDF fetch failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      // Open in a new tab — mobile Safari lets the user tap "Share" to save
      // or print. Desktop browsers render the PDF inline.
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open statement");
    }
  };

  const openExpenseModal = () => {
    setExpForm({ amount: "", category: "", note: "", file: null, preview: null, company_card_id: "" });
    setExpOpen(true);
  };

  const onFile = (f) => {
    if (!f) { setExpForm((p) => ({ ...p, file: null, preview: null })); return; }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Receipt must be under 8MB");
      return;
    }
    const url = URL.createObjectURL(f);
    setExpForm((p) => ({ ...p, file: f, preview: url }));
  };

  const submitExpense = async () => {
    const amt = parseFloat(expForm.amount);
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Enter an amount greater than 0"); return; }
    if (!expForm.file) { toast.error("Attach a photo of the receipt"); return; }
    setExpBusy(true);
    try {
      const fd = new FormData();
      fd.append("amount", String(amt));
      fd.append("category", expForm.category || "");
      fd.append("note", expForm.note || "");
      fd.append("company_card_id", expForm.company_card_id || "");
      fd.append("file", expForm.file);
      fd.append("note", expForm.note || "");
      fd.append("file", expForm.file);
      await dax().post("/driver-portal/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt submitted — your dispatcher will review it");
      setExpOpen(false);
      await loadExpenses();
      setTab("expenses");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setExpBusy(false); }
  };

  if (!me) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col">
        <Toaster position="top-center" richColors />
        <div className="flex-1 flex items-center justify-center px-5 py-10">
          <div className="w-full max-w-sm">
            <div className="text-center mb-6">
              <div className="inline-flex w-14 h-14 rounded-full bg-amber-500 items-center justify-center mb-3">
                <FileText className="text-slate-900" size={24} />
              </div>
              <h1 className="text-2xl font-semibold">Driver Portal</h1>
              <p className="text-sm text-slate-400 mt-1">Enter your PIN to view pay stubs and submit receipts.</p>
            </div>
            {(!w || !d) && (
              <div className="rounded-lg border border-rose-700 bg-rose-900/30 text-rose-200 p-4 text-sm mb-4">
                This portal link is missing your fleet or driver code. Ask your dispatcher for the exact link they generated for you.
              </div>
            )}
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300">PIN</Label>
                <Input
                  type="tel" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  placeholder="••••••"
                  className="bg-slate-800 border-slate-700 text-white text-center text-xl tracking-widest font-mono h-14"
                  data-testid="driver-portal-pin"
                />
              </div>
              <Button
                onClick={login}
                disabled={busy || !w || !d}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 h-12 text-base font-semibold"
                data-testid="driver-portal-login-btn"
              >
                {busy ? "Checking…" : "Sign in"}
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center mt-6">
              Having trouble? Ask your dispatcher to resend the portal link + PIN.
              <br />
              <Link to="/home" className="text-amber-400 hover:underline mt-2 inline-block">About Dispatch Pay →</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalEarned = statements.reduce((s, x) => s + Number(x.totals?.net || 0), 0);
  const pendingCount = expenses.filter((x) => x.status === "pending").length;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Toaster position="top-center" richColors />
      <header className="bg-slate-900 text-white px-5 py-4">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">{me.company_name || "Fleet"}</div>
            <div className="text-[11px] text-slate-400">{me.driver.name} · {me.driver.display_id}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="driver-portal-logout">
            <LogOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-6" data-testid="driver-portal-home">
        <div className="rounded-lg bg-amber-500 text-slate-900 p-5 mb-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Total pay received</div>
          <div className="text-3xl font-bold mt-1">${totalEarned.toFixed(2)}</div>
          <div className="text-xs mt-1 opacity-80">Across {statements.length} statement{statements.length === 1 ? "" : "s"}</div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-4">
          <button
            onClick={() => setTab("stubs")}
            data-testid="dp-tab-stubs"
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "stubs" ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500"}`}
          >Pay Stubs</button>
          <button
            onClick={() => setTab("expenses")}
            data-testid="dp-tab-expenses"
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${tab === "expenses" ? "border-amber-500 text-slate-900" : "border-transparent text-slate-500"}`}
          >
            My Expenses
            {pendingCount > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold">{pendingCount}</span>}
          </button>
        </div>

        {tab === "stubs" && (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Pay stubs</div>
            <div className="space-y-3">
              {statements.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                  No pay stubs yet. Your dispatcher will post them here once payroll closes.
                </div>
              )}
              {statements.map((s) => (
                <div key={s.statement_id} className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid={`stmt-${s.statement_number}`}>
                  <div>
                    <div className="font-mono text-sm font-semibold text-slate-900">{s.statement_number}</div>
                    <div className="text-xs text-slate-500">{s.period_start} → {s.period_end} · Paid {s.pay_date}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Status: <span className="font-semibold text-slate-700">{s.status || "draft"}</span></div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Net</div>
                    <div className="font-mono font-bold text-slate-900">${Number(s.totals?.net || 0).toFixed(2)}</div>
                    <Button size="sm" variant="outline" className="mt-1 h-7 text-[11px]" onClick={() => downloadPdf(s.statement_id)}>
                      <Download size={12} className="mr-1" /> View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "expenses" && (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">My receipts</div>
              <Button size="sm" onClick={openExpenseModal} className="bg-slate-900 hover:bg-slate-800 h-8 text-xs" data-testid="dp-new-expense">
                <Plus size={14} className="mr-1" /> Submit receipt
              </Button>
            </div>
            <div className="space-y-3">
              {expenses.length === 0 && (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                  No receipts yet. Tap <strong>Submit receipt</strong> to snap one — your dispatcher will review it and (if approved) add it to your next pay stub.
                </div>
              )}
              {expenses.map((e) => (
                <div key={e.expense_id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`exp-${e.expense_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{e.category || "Receipt"}</div>
                      {e.note && <div className="text-xs text-slate-500 mt-0.5 truncate">{e.note}</div>}
                      <div className="text-[11px] text-slate-400 mt-1 font-mono">{(e.submitted_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono font-bold text-slate-900">${Number(e.amount || 0).toFixed(2)}</div>
                      <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${(STATUS_PILL[e.status] || "").replace("/15","/80").replace("text-amber-400","text-amber-700").replace("text-emerald-400","text-emerald-700").replace("text-rose-400","text-rose-700").replace("text-sky-400","text-sky-700")}`}>
                        {e.status}
                        {e.status === "approved" && e.auto_reimburse && " · auto-pay"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Submit expense modal */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ReceiptIcon size={18} /> New receipt</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input
                type="number" step="0.01" inputMode="decimal" placeholder="e.g. 42.75"
                value={expForm.amount}
                onChange={(e) => setExpForm((p) => ({ ...p, amount: e.target.value }))}
                className="h-11 text-right font-mono text-base"
                data-testid="dp-exp-amount"
              />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Input
                placeholder="Fuel, tolls, lumper, maintenance… (type anything)"
                value={expForm.category}
                onChange={(e) => setExpForm((p) => ({ ...p, category: e.target.value }))}
                className="h-10 text-sm"
                data-testid="dp-exp-category"
              />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea
                rows={2} placeholder="Vendor, trip number, anything your dispatcher needs"
                value={expForm.note}
                onChange={(e) => setExpForm((p) => ({ ...p, note: e.target.value }))}
                className="text-sm resize-none"
                data-testid="dp-exp-note"
              />
            </div>
            {/* Company-card picker. Only shows when the admin loaded cards onto
                this driver. Choosing one tags the receipt as COMPANY-paid --
                admin will see it but it never touches the driver's paycheck. */}
            {Array.isArray(me?.driver?.cards) && me.driver.cards.length > 0 && (
              <div>
                <Label className="text-xs">Paid with company card?</Label>
                <select
                  value={expForm.company_card_id}
                  onChange={(e) => setExpForm((p) => ({ ...p, company_card_id: e.target.value }))}
                  className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  data-testid="dp-exp-company-card"
                >
                  <option value="">No — paid out of pocket (reimburse / deduct)</option>
                  {me.driver.cards.map((c) => (
                    <option key={c.card_id} value={c.card_id}>
                      {c.label} · •••{c.last4}
                    </option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Tagging a company card logs this as a company expense — it won't deduct from or reimburse against your pay.
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Receipt photo</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <label className="flex flex-col items-center justify-center gap-1 h-20 rounded-md border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50 text-slate-700 text-xs">
                  <Camera size={18} />
                  <span>Snap photo</span>
                  <input type="file" accept="image/*" capture="environment"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    className="hidden" data-testid="dp-exp-camera"
                  />
                </label>
                <label className="flex flex-col items-center justify-center gap-1 h-20 rounded-md border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50 text-slate-700 text-xs">
                  <Upload size={18} />
                  <span>Upload file</span>
                  <input type="file" accept="image/*"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    className="hidden" data-testid="dp-exp-upload"
                  />
                </label>
              </div>
              {expForm.preview && (
                <div className="mt-2">
                  <img src={expForm.preview} alt="Receipt preview" className="w-full max-h-48 object-contain rounded border border-slate-200 bg-slate-50" />
                </div>
              )}
            </div>
            <Button onClick={submitExpense} disabled={expBusy} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 h-11 font-semibold" data-testid="dp-exp-submit">
              {expBusy ? "Submitting…" : "Submit receipt"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
